import { HttpParams } from "@angular/common/http";
import { Component, Inject, ViewChild, forwardRef } from "@angular/core";
import { NgForm } from "@angular/forms";
import { DateAdapter } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import * as moment from "moment";
import * as moment_tz from 'moment-timezone';
import { take } from "rxjs";
import { Permissions } from "../../common/constants";
import { WorkOrder, WorkOrderStatus } from "../../model";
import { AuthenticationService } from "../../service/authentication.service";
import { ContextService } from "../../service/context.service";
import { StatisticService } from "../../service/statistic.service";
import { ThingContextService } from "../../service/thing-context.service";
import { ConfirmDialog } from "../../shared/confirm-dialog/confirm-dialog.component";

@Component({
    selector: 'work-order-edit-dialog',
    template: require('./work-order-edit-dialog.component.html')
})
export class WorkOrderEditDialogComponent {

    @ViewChild('form') form: NgForm;

    workOrder: WorkOrder;
    statusOnly: boolean;
    startDate: moment.Moment;
    startTime: string;
    endDate: moment.Moment;
    endTime: string;
    canComplete: boolean;
    isManager: boolean;
    isExecutor: boolean;
    showExtraProperties: boolean;
    isHistorical: boolean;
    showMachineWorkingHours: boolean;

    private timezone: string;

    constructor(
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<WorkOrderEditDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => StatisticService)) private statisticService: StatisticService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => MatDialog)) protected dialog: MatDialog,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => DateAdapter)) private dateAdapter: DateAdapter<moment.Moment>,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService
    ) {
        this.isHistorical = data.isHistorical;
        this.statusOnly = data.statusOnly;
        this.workOrder = data.workOrder;
        this.initTimeVariables();
        this.checkCanComplete();
        this.isManager = this.authenticationService.hasPermission(Permissions.WORK_ORDER_MANAGER);
        this.isExecutor = this.authenticationService.hasPermission(Permissions.WORK_ORDER_EXECUTOR);
        this.dateAdapter.setLocale(this.authenticationService.getUser().locale || 'en');
        this.showExtraProperties = this.authenticationService.getTenant().workOrderExtraPropertiesEnabled;
        const thingProperties = this.thingContextService.getCurrentThing()?.properties || {};
        this.thingContextService.getMetricByName('working_hours').then(workingHoursMetric => {
            this.showMachineWorkingHours = !this.isHistorical && !Object.keys(thingProperties).includes('working_hours') && !workingHoursMetric;
        });
    }

    private initTimeVariables(): void {
        this.timezone = this.contextService.getTimezone();
        if (this.workOrder.startTimestamp) {
            this.startDate = moment_tz.tz(this.workOrder.startTimestamp, this.timezone);
        } else if (this.statusOnly && this.workOrder.status == WorkOrderStatus.EXECUTION) {
            this.startDate = moment();
        }
        this.startTime = this.getTimeString(this.startDate);
        if (this.workOrder.endTimestamp) {
            this.endDate = moment_tz.tz(this.workOrder.endTimestamp, this.timezone);
        } else if (this.statusOnly) {
            this.endDate = moment();
        }
        this.endTime = this.getTimeString(this.endDate);
    }

    private getTimeString(moment: moment.Moment): string {
        if (!moment) {
            return null;
        }
        return this.formatString(moment.hours()) + ':' + this.formatString(moment.minutes());
    }

    private formatString(num: number) {
        return num.toString().length == 1 ? '0' + num : num;
    }

    private checkCanComplete(): void {
        let params = new HttpParams();
        params = params.set('resource', 'action');
        params = params.set('aggregation', 'COUNT');
        params = params.set('thingId', this.workOrder.thingId);
        params = params.set('workOrderId', `eq;${this.workOrder.id}`);
        params = params.set('executionState', 'exists;false');
        this.statisticService.getStatisticPromise(null, params).then(results => {
            const count = results[0];
            this.canComplete = !count?.value;
        });
    }

    save(): void {
        const formValue = this.form.form.getRawValue();
        let body;
        if (this.statusOnly) {
            const newStatus = formValue.status as WorkOrderStatus;
            const newFields = { status: newStatus } as any;
            if (this.workOrder.status == WorkOrderStatus.DRAFT && newStatus == WorkOrderStatus.APPROVED) {
                newFields.serviceId = formValue.serviceId;
                newFields.transportDocumentCode = formValue.transportDocumentCode;
            }
            if (newStatus == WorkOrderStatus.APPROVED) {
                if (formValue.startDate) {
                    newFields.startTimestamp = this.getTimestamp(formValue.startDate, formValue.startTime);
                }
            } else if (newStatus == WorkOrderStatus.EXECUTION) {
                newFields.startTimestamp = this.getTimestamp(formValue.startDate, formValue.startTime);
            } else if (newStatus == WorkOrderStatus.COMPLETED) {
                newFields.startTimestamp = this.getTimestamp(formValue.startDate, formValue.startTime);
                newFields.endTimestamp = this.getTimestamp(formValue.endDate, formValue.endTime);
            }
            if (this.showMachineWorkingHours) {
                newFields.machineWorkingHours = formValue.machineWorkingHours;
            }
            body = Object.assign({}, this.workOrder, newFields);
        } else {
            body = formValue;
            body.status = this.workOrder.status;
            if (body.startDate) {
                body.startTimestamp = this.getTimestamp(body.startDate, body.startTime);
                delete body.startDate;
                delete body.startTime;
            }
            if (body.endDate) {
                body.endTimestamp = this.getTimestamp(body.endDate, body.endTime);
                delete body.endDate;
                delete body.endTime;
            }
        }
        if (this.workOrder.status == WorkOrderStatus.EXECUTION && body.status == WorkOrderStatus.APPROVED) {
            this.openConfirmDialog(body);
        } else {
            this.dialogRef.close(body);
        }
    }
    private getTimestamp(date: moment.Moment, time: string) {
        const localeDate = moment_tz.tz(date.format("YYYY-MM-DD"), 'YYYY-MM-DD', this.timezone);
        let hours = Number(time?.substring(0, 2));
        let minutes = Number(time?.substring(3, 5));
        let result = localeDate.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
        return result.valueOf();
    }


    private openConfirmDialog(body: any): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "changeWorkOrderStatusAlertTitle",
            message: "changeWorkOrderStatusAlertMessage"
        }
        this.dialog.open(ConfirmDialog, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.dialogRef.close(body);
            }
        });
    }


    setStartNow(): void {
        const now = moment_tz.tz(this.timezone);
        this.form.form.controls['startDate'].setValue(now);
        this.form.form.controls['startTime'].setValue(this.getTimeString(now));
        this.form.form.markAsDirty();
    }

    setEndNow(): void {
        const now = moment_tz.tz(this.timezone);
        this.form.form.controls['endDate'].setValue(now);
        this.form.form.controls['endTime'].setValue(this.getTimeString(now));
        this.form.form.markAsDirty();
    }
}